import { Box, Tooltip, Typography } from '@material-ui/core'
import { fade, makeStyles } from '@material-ui/core/styles'
import React, { useState } from 'react'

/**
 * Month calendar of a period in the Checklists › History look (Satya, 26 Oct:
 * "more diagrammatic … like the checklist"): a box per day with its number
 * and one dot per thing that happened, coloured by how it went. Tap a day to
 * list what its dots are. Use it wherever a list's rows each fall on a day;
 * lists with no date (onboarding steps, open issues) don't get one.
 *
 * marks  [{date: 'YYYY-MM-DD', text, state: good | warn | bad | info}]
 * from, to  the period (ISO); days outside it are faded
 * legend  [[state, label]] for DayCalendarLegend — only states that occur
 * onPick  optional (iso | null): a click filters the page's own list to the
 *         day. Either way, hovering a day shows its dots in a pop-over
 *         (Satya, 29 Oct: hover, as on Lesson Planning, on every calendar)
 */

const useStyles = makeStyles((theme) => {
  const line = theme.palette.divider
  const muted = fade(theme.palette.text.primary, 0.55)
  const faint = fade(theme.palette.text.primary, 0.38)
  const accent = theme.palette.primary.main
  return {
    title: { fontWeight: 700, fontSize: 13, margin: '4px 0 6px' },
    month: {
      fontSize: 15,
      fontWeight: 600,
      textAlign: 'center',
      margin: '8px 0'
    },
    weekdays: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 6,
      marginBottom: 4
    },
    weekday: {
      textAlign: 'center',
      fontSize: 11,
      fontWeight: 600,
      color: faint
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 6,
      marginBottom: 8
    },
    day: {
      minHeight: 58,
      borderRadius: 10,
      border: `1px solid ${line}`,
      padding: theme.spacing(0.5, 0.75),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      background: theme.palette.background.paper,
      font: 'inherit',
      textAlign: 'left',
      cursor: 'pointer',
      '&:hover': { borderColor: accent }
    },
    picked: { borderColor: accent, boxShadow: `0 0 0 1px ${accent}` },
    hidden: { visibility: 'hidden' },
    outside: {
      opacity: 0.45,
      cursor: 'default',
      '&:hover': { borderColor: line }
    },
    num: { fontSize: 12, fontWeight: 600, color: muted },
    dots: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 3,
      alignItems: 'center',
      minHeight: 14
    },
    dot: {
      width: 9,
      height: 9,
      borderRadius: '50%',
      flex: '0 0 auto',
      display: 'inline-block',
      boxSizing: 'border-box'
    },
    more: { fontSize: 10, fontWeight: 700, color: muted },
    good: { background: theme.palette.text.success || '#3f9d5b' },
    bad: { background: theme.palette.error.main },
    warn: { background: '#b57907' },
    info: { background: 'transparent', border: `1.5px solid ${accent}` },
    list: {
      border: `1px solid ${line}`,
      borderRadius: 10,
      padding: theme.spacing(1, 1.5),
      marginBottom: 12,
      fontSize: 13
    },
    listHead: { fontWeight: 700, marginBottom: 4 },
    tip: {
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${line}`,
      borderRadius: 10,
      boxShadow: theme.shadows[3],
      padding: theme.spacing(1, 1.5),
      fontSize: 13,
      maxWidth: 420
    },
    listRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      padding: '3px 0'
    },
    legend: {
      display: 'flex',
      gap: theme.spacing(2),
      flexWrap: 'wrap',
      margin: '4px 0 12px',
      fontSize: 12,
      color: muted,
      alignItems: 'center'
    },
    legendDot: { verticalAlign: -1, marginRight: 5 }
  }
})

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`
const dateOf = (iso) => new Date(`${iso}T00:00:00`)

// Monday-started weeks covering one month (cells outside it are hidden).
const monthCells = (m) => {
  const d = new Date(m.getFullYear(), m.getMonth(), 1)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  const last = new Date(m.getFullYear(), m.getMonth() + 1, 0)
  const cells = []
  while (d <= last || d.getDay() !== 1) {
    cells.push(new Date(d))
    d.setDate(d.getDate() + 1)
  }
  return cells
}

const DayCalendar = ({ title, marks, from, to, onPick }) => {
  const classes = useStyles()
  const [picked, setPicked] = useState(null)
  const byDate = {}
  marks.forEach((m) => {
    byDate[m.date] = [...(byDate[m.date] || []), m]
  })
  const dates = Object.keys(byDate).sort()
  const first = from || dates[0]
  const last = to || dates[dates.length - 1]
  if (!first || !last) return null
  const months = []
  const m = new Date(dateOf(first).getFullYear(), dateOf(first).getMonth(), 1)
  while (isoOf(m) <= last) {
    months.push(new Date(m))
    m.setMonth(m.getMonth() + 1)
  }
  const dotClass = (state) => `${classes.dot} ${classes[state] || classes.info}`
  return (
    <div>
      {title && <Typography className={classes.title}>{title}</Typography>}
      {months.map((month) => (
        <div key={isoOf(month)}>
          <Typography className={classes.month}>
            {month.toLocaleDateString('en-IN', {
              month: 'long',
              year: 'numeric'
            })}
          </Typography>
          <Box className={classes.weekdays}>
            {WEEKDAYS.map((w) => (
              <span key={w} className={classes.weekday}>
                {w}
              </span>
            ))}
          </Box>
          <Box className={classes.grid}>
            {monthCells(month).map((d) => {
              const iso = isoOf(d)
              if (d.getMonth() !== month.getMonth())
                return <span key={iso} className={classes.hidden} />
              const hits = byDate[iso] || []
              const out = iso < first || iso > last
              const cell = (
                <button
                  type="button"
                  key={iso}
                  data-testid={`cal-${iso}`}
                  style={onPick ? undefined : { cursor: 'default' }}
                  className={[
                    classes.day,
                    out ? classes.outside : '',
                    picked === iso ? classes.picked : ''
                  ].join(' ')}
                  onClick={
                    out || !onPick
                      ? undefined
                      : () => {
                          const next = picked === iso ? null : iso
                          setPicked(next)
                          if (onPick) onPick(next)
                        }
                  }
                >
                  <span className={classes.num}>{d.getDate()}</span>
                  <span className={classes.dots}>
                    {/* Every dot, wrapping: no "+n" (Satya, 29 Oct). */}
                    {hits.map((h, i) => (
                      <span key={i} className={dotClass(h.state)} />
                    ))}
                  </span>
                </button>
              )
              if (out || !hits.length) return cell
              return (
                <Tooltip
                  key={iso}
                  placement="top"
                  classes={{ tooltip: classes.tip }}
                  title={
                    <>
                      <div className={classes.listHead}>
                        {d.toLocaleDateString('en-IN', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                      </div>
                      {hits.map((h, i) => (
                        <div key={i} className={classes.listRow}>
                          <span className={dotClass(h.state)} />
                          {h.text}
                        </div>
                      ))}
                    </>
                  }
                >
                  {cell}
                </Tooltip>
              )
            })}
          </Box>
        </div>
      ))}
    </div>
  )
}

// One legend for one or more calendars: only the states that occur.
export const DayCalendarLegend = ({ marks, legend }) => {
  const classes = useStyles()
  const used = new Set(marks.map((m) => m.state))
  const items = legend.filter(([state]) => used.has(state))
  if (!items.length) return null
  return (
    <div className={classes.legend}>
      {items.map(([state, text]) => (
        <span key={state}>
          <span
            className={`${classes.dot} ${classes[state]} ${classes.legendDot}`}
          />
          {text}
        </span>
      ))}
    </div>
  )
}

export default DayCalendar
